import { useState, useEffect, useContext } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faMinus,
  faTrashCan,
  faSlashForward,
} from "@fortawesome/pro-solid-svg-icons";
import { toast, Slide } from "react-toastify";
import styles from "./AddToCart.module.scss";
import { formatCurrency } from "../../utils/formatCurrency";
import { useShoppingCart } from "../../context/shopping-cart-context";
import StoreContext from "../../context/store-context";

const WEIGHT_ABBREVIATIONS = {
  "half gram": "0.5g",
  "gram": "1g",
  "two gram": "2g",
  "eighth ounce": "3.5g",
  "quarter ounce": "7g",
  "half ounce": "14g",
  "ounce": "28g",
  "default": "each",
};

const abbreviateWeight = (weight) =>
  WEIGHT_ABBREVIATIONS[weight] || WEIGHT_ABBREVIATIONS["default"];

const getDefaultWeight = (availableWeights) => {
  const preferredWeight = "eighth ounce";
  if (availableWeights.includes(preferredWeight)) {
    return preferredWeight;
  }
  return availableWeights.length > 0 ? availableWeights[0] : "each";
};

export default function Price({ hit, className, weight }) {
  const singleWeight = weight;
  const router = useRouter();

  const toWeightKey = (weight) =>
    weight ? `price_${weight.replace(/ /g, "_")}` : "";
  const toDiscountedWeightKey = (weight) =>
    weight ? `discounted_price_${weight.replace(/ /g, "_")}` : "";

  const [selectedWeight, setSelectedWeight] = useState(
    getDefaultWeight(hit.available_weights)
  );
  const [price, setPrice] = useState(
    hit[toWeightKey(getDefaultWeight(hit.available_weights))] || hit.price_each
  );
  const [discountedPrice, setDiscountedPrice] = useState(
    hit[toDiscountedWeightKey(getDefaultWeight(hit.available_weights))] ||
    hit.discounted_price_each
  );

  useEffect(() => {
    const initialWeight = router.query.weight && hit.available_weights.includes(router.query.weight)
      ? router.query.weight
      : singleWeight && hit.available_weights.includes(singleWeight)
        ? singleWeight
        : getDefaultWeight(hit.available_weights);

    setSelectedWeight(initialWeight);

    const newWeightPriceKey = toWeightKey(initialWeight);
    const newDiscountedWeightPriceKey = toDiscountedWeightKey(initialWeight);
    setPrice(hit[newWeightPriceKey] || hit.price_each);
    setDiscountedPrice(hit[newDiscountedWeightPriceKey] || hit.discounted_price_each);
  }, [router.query.weight, singleWeight, hit.available_weights]);

  const weights = hit.available_weights.map((weight) => ({
    weight,
    weight_price: toWeightKey(weight),
    discounted_weight_price: toDiscountedWeightKey(weight),
  }));

  const handleChange = (e) => {
    setSelectedWeight(e.target.value);
    const weightPriceKey = toWeightKey(e.target.value);
    setPrice(hit[weightPriceKey] || hit.price_each);

    const discountedWeightPriceKey = toDiscountedWeightKey(e.target.value);
    setDiscountedPrice(
      hit[discountedWeightPriceKey] || hit.discounted_price_each
    );
  };

  return (
    <div className={`${styles.addToCart} ${className}`}>
      <div>
        <span
          className={`${styles.amount} ${discountedPrice ? styles.discounted : ""}`}
          attribute="price"
        >
          {formatCurrency(price)}
        </span>

        {discountedPrice && (
          <div className={styles.price}>
            <span className={styles.amount} attribute="price">
              {formatCurrency(discountedPrice)}
            </span>
          </div>
        )}

        {
          singleWeight ? (
            <span className={styles.weight}>
              <FontAwesomeIcon icon={faSlashForward} />
              {abbreviateWeight(singleWeight.replace(/_/g, " "))}
            </span>
          ) : (
            hit.available_weights.length > 1 ? (
              <>
                <label htmlFor={hit.product_id} className="visually-hidden">Select Weight:</label>
                <select
                  name="weight"
                  className={styles.weight_select}
                  id={hit.product_id}
                  value={selectedWeight}
                  onChange={handleChange}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  {weights.map(({ weight }) => (
                    <option key={weight} value={weight}>
                      {hit.amount || abbreviateWeight(weight)}
                    </option>
                  ))}
                </select>
              </>
            ) : hit.available_weights.length === 1 ? (
              <span className={styles.weight}>
                <FontAwesomeIcon icon={faSlashForward} />
                {hit.amount || abbreviateWeight(hit.available_weights[0])}
              </span>
            ) : (
              <span className={styles.weight}>
                <FontAwesomeIcon icon={faSlashForward} />
                {hit.amount || "Each"}
              </span>
            )
          )
        }
      </div>
      <AddToCartButton
        hit={hit}
        selectedWeight={selectedWeight?.replace(/ /g, "_")}
      />
    </div>
  );
}

function AddToCartButton({ hit, selectedWeight }) {
  const {
    getItemQuantity,
    increaseCartQuantity,
    decreaseCartQuantity,
    removeFromCart,
    setCartModal,
  } = useShoppingCart();
  const { orderType, setModal } = useContext(StoreContext);
  const [loadButton, setLoadButton] = useState(false);

  useEffect(() => {
    setLoadButton(true);
  }, []);

  const quantity = getItemQuantity(hit.product_id, selectedWeight);

  const handleClick = (e) => {
    if (orderType === "Browsing") {
      setModal(true);
    } else {
      e.preventDefault();
      e.stopPropagation();
      increaseCartQuantity(hit.product_id, selectedWeight);
      setCartModal(true);
    }
  };

  return (
    <div className={styles.buttonContainer}>
      {loadButton ? (
        <>
          {quantity === 0 ? (
            <button
              className={`openLocationButton ${styles.add_to_bag}`}
              aria-label={orderType !== "Browsing" ? "Add to Bag" : "Check Availability"}
              onClick={(e) => handleClick(e)}
            >
              {orderType !== "Browsing" ? "Add to Bag" : "Check Availability"}
            </button>
          ) : (
            <div
              className={styles.modify_cart}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {quantity == 1 ? (
                <button
                  className={styles.trash}
                  onClick={(e) => {
                    removeFromCart(hit.product_id, selectedWeight);
                  }}
                  aria-label="remove item"
                >
                  <FontAwesomeIcon icon={faTrashCan} />
                </button>
              ) : (
                <button
                  className={quantity < 1 ? styles.deactive : null}
                  onClick={(e) => {
                    decreaseCartQuantity(hit.product_id, selectedWeight);
                  }}
                  aria-label="decrease amount"
                >
                  <FontAwesomeIcon icon={faMinus} />
                </button>
              )}
              {quantity} in bag
              <button
                className={
                  quantity === hit.max_cart_quantity ? styles.deactive : null
                }
                onClick={(e) => {
                  increaseCartQuantity(hit.product_id, selectedWeight);
                }}
                aria-label="increase amount"
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>
          )}
        </>
      ) : (
        <button
          className={`openLocationButton ${styles.add_to_bag}`}
          aria-label="Loading..."
          onClick={(e) => handleClick(e)}
        >
          Loading...
        </button>
      )}
    </div>
  );
}
